import { useOktaAuth } from "@okta/okta-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import LoginCallbackRoute from "@/auth/LoginCallbackRoute";
import Loading from "@/pages/Loading";
import { AppRoute } from "@/types/routes";

interface RequireAuthProps {
  readonly children: React.ReactNode;
}

/**
 * Gates the whole app behind Okta sign-in (redirect flow), replacing Amplify's `withAuthenticator`.
 * Renders the login-callback exchange page directly (no router `<Route>` needed) while `/login/callback`
 * is the current path, otherwise redirects unauthenticated visitors to Okta and blocks rendering `children`
 * until `authState.isAuthenticated` is true.
 */
const RequireAuth = ({ children }: RequireAuthProps) => {
  const { authState, oktaAuth } = useOktaAuth();
  const location = useLocation();
  const isCallbackRoute = location.pathname === AppRoute.LoginCallback;

  useEffect(() => {
    if (isCallbackRoute || !authState || authState.isAuthenticated) {
      return;
    }

    oktaAuth.setOriginalUri(`${location.pathname}${location.search}`);
    void oktaAuth.signInWithRedirect();
  }, [authState, isCallbackRoute, location.pathname, location.search, oktaAuth]);

  if (isCallbackRoute) {
    return <LoginCallbackRoute />;
  }

  if (!authState?.isAuthenticated) {
    return <Loading />;
  }

  return children;
};

export default RequireAuth;
